import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  Users, 
  Package, 
  DollarSign, 
  TrendingUp,
  UserCheck,
  UserX,
  Plus,
  Upload,
  Settings,
  AlertCircle,
  Wallet,
  Search,
  Bell,
  Clock,
  CheckCircle,
  Activity,
  Smartphone,
  ToggleRight,
  ToggleLeft,
  Zap,
  BarChart3
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { UserService } from '../../services/userService';
import { ProductService } from '../../services/productService';

import LoadingSpinner from '../common/LoadingSpinner';
import { useNotification } from '../common/Notification';
import { useNotifications } from '../../contexts/NotificationContext';

const AdminDashboard = () => {
  const { currentUser } = useAuth();
  const { addNotification } = useNotification();
  const { notifications, unreadCount } = useNotifications();
  
  const [stats, setStats] = useState({
    users: { total: 0, active: 0, pending: 0 },
    products: { total: 0, active: 0 },
    transactions: { total: 0, totalAmount: 0 },
    pendingUsers: [],
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  
  // États pour la recharge rapide
  const [quickRecharge, setQuickRecharge] = useState({
    searchTerm: '',
    selectedUser: null,
    amount: '',
    note: '',
    isSearching: false,
    searchResults: [],
    isRecharging: false
  });
  


  useEffect(() => {
    loadDashboardData();
  }, []);



  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Charger les statistiques des utilisateurs
      const userStats = await UserService.getUsersStats();
      const pendingUsers = await UserService.getPendingResellers();
      
      // Charger les produits
      const products = await ProductService.getAllProducts();
      const activeProducts = products.filter(p => p.status === 'active');

      // Créer une activité récente basée sur les revendeurs en attente
      const recentActivity = [
        ...pendingUsers.map(user => ({
          id: user.id,
          type: 'user_registration',
          title: 'Nouveau revendeur en attente',
          description: `${user.firstName} ${user.lastName} - ${user.company}`,
          timestamp: user.createdAt,
          status: 'pending'
        }))
      ].sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)).slice(0, 10);
      
      setStats({
        users: userStats,
        products: {
          total: products.length,
          active: activeProducts.length
        },
        transactions: {
          total: 0, // À implémenter si nécessaire
          totalAmount: 0
        },
        pendingUsers: pendingUsers.slice(0, 5),
        recentActivity: recentActivity
      });
    } catch (error) {
      console.error('Erreur lors du chargement du dashboard admin:', error);
      addNotification('error', 'Erreur de chargement', 'Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleActivateUser = async (userId) => {
    setActionLoading(prev => ({ ...prev, [`activate-${userId}`]: true }));
    
    try {
      await UserService.activateReseller(userId, currentUser.uid);
      addNotification('success', 'Utilisateur activé', 'Utilisateur activé avec succès');
      await loadDashboardData(); // Recharger les données
    } catch (error) {
      console.error('Erreur lors de l\'activation:', error);
      addNotification('error', 'Erreur d\'activation', error.message || 'Erreur lors de l\'activation');
    } finally {
      setActionLoading(prev => ({ ...prev, [`activate-${userId}`]: false }));
    }
  };



  const handleDeactivateUser = async (userId) => {
    const reason = prompt('Raison de la désactivation (optionnel):');
    if (reason === null) return; // Annulé
    
    setActionLoading(prev => ({ ...prev, [`deactivate-${userId}`]: true }));
    
    try {
      await UserService.deactivateReseller(userId, currentUser.uid, reason);
      addNotification('success', 'Utilisateur désactivé', 'Utilisateur désactivé avec succès');
      await loadDashboardData();
    } catch (error) {
      console.error('Erreur lors de la désactivation:', error);
      addNotification('error', 'Erreur de désactivation', error.message || 'Erreur lors de la désactivation');
    } finally {
      setActionLoading(prev => ({ ...prev, [`deactivate-${userId}`]: false }));
    }
  };

  // Fonctions pour la recharge rapide
  const searchUsers = async (searchTerm) => {
    if (!searchTerm.trim()) {
      setQuickRecharge(prev => ({ ...prev, searchResults: [], isSearching: false }));
      return;
    }

    setQuickRecharge(prev => ({ ...prev, isSearching: true }));
    
    try {
      const users = await UserService.searchResellers(searchTerm);
      setQuickRecharge(prev => ({ 
        ...prev, 
        searchResults: users.slice(0, 5), // Limiter à 5 résultats
        isSearching: false 
      }));
    } catch (error) {
      console.error('Erreur lors de la recherche:', error);
      setQuickRecharge(prev => ({ ...prev, isSearching: false, searchResults: [] }));
    }
  };

  const handleQuickRecharge = async () => {
    if (!quickRecharge.selectedUser || !quickRecharge.amount) {
      addNotification('error', 'Données manquantes', 'Veuillez sélectionner un utilisateur et saisir un montant');
      return;
    }

    const amount = parseFloat(quickRecharge.amount);
    if (isNaN(amount) || amount <= 0) {
      addNotification('error', 'Montant invalide', 'Veuillez saisir un montant valide');
      return;
    }

    setQuickRecharge(prev => ({ ...prev, isRecharging: true }));
    
    try {
      await UserService.rechargeUserBalance(
        quickRecharge.selectedUser.id,
        amount,
        quickRecharge.note || 'Recharge rapide depuis le dashboard admin',
        currentUser.uid
      );
      
      addNotification('success', 'Recharge effectuée', `Solde rechargé avec succès pour ${quickRecharge.selectedUser.firstName} ${quickRecharge.selectedUser.lastName}`);
      
      // Réinitialiser le formulaire
      setQuickRecharge({
        searchTerm: '',
        selectedUser: null,
        amount: '',
        note: '',
        isSearching: false,
        searchResults: [],
        isRecharging: false
      });
      
    } catch (error) {
      console.error('Erreur lors de la recharge:', error);
      addNotification('error', 'Erreur de recharge', error.message || 'Erreur lors de la recharge');
    } finally {
      setQuickRecharge(prev => ({ ...prev, isRecharging: false }));
    }
  };



  const formatCurrency = (amount) => {
    return `${amount.toLocaleString('fr-DZ')} DA`;
  };

  const handleUserSelect = (user) => {
    setQuickRecharge(prev => ({
      ...prev,
      selectedUser: user,
      searchTerm: `${user.firstName} ${user.lastName}`,
      searchResults: []
    }));
  };

  const handleSearchChange = (value) => {
    setQuickRecharge(prev => ({ 
      ...prev, 
      searchTerm: value,
      selectedUser: null 
    }));
    
    // Débounce la recherche
    clearTimeout(window.searchTimeout);
    window.searchTimeout = setTimeout(() => {
      searchUsers(value);
    }, 300);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Main Content */}
      <div className="max-w-7xl mx-auto">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Tableau de bord administrateur
          </h2>
          <p className="text-gray-600">
            Gérez les utilisateurs, produits et transactions de la plateforme.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {/* Total Users */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Utilisateurs totaux</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.users.total}
                </p>
                <p className="text-xs text-green-600 mt-1">
                  {stats.users.active} actifs • {stats.users.pending} en attente
                </p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </div>

          {/* Products */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Produits</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.products.total}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {stats.products.active} actifs
                </p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <Package className="w-6 h-6 text-green-600" />
              </div>
            </div>
          </div>

          {/* Notifications */}
          <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Notifications</p>
                <p className="text-2xl font-bold text-gray-900">{unreadCount}</p>
                <p className="text-xs text-purple-600 mt-1">
                  Non lues
                </p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Bell className="w-6 h-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
        {/* Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-8">
          {/* Pending Users */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Comptes en attente</h3>
                <Link
                  to="/admin/users"
                  className="text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
                >
                  Voir tout
                </Link>
              </div>
            </div>
            <div className="p-6">
              {stats.pendingUsers.length > 0 ? (
                <div className="space-y-4">
                  {stats.pendingUsers.map((user) => (
                    <div key={user.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {user.firstName} {user.lastName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {user.company} • {user.email}
                        </p>
                      </div>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleActivateUser(user.id)}
                          disabled={actionLoading[`activate-${user.id}`]}
                          className="p-2 text-success-600 hover:bg-success-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Activer"
                        >
                          {actionLoading[`activate-${user.id}`] ? (
                            <LoadingSpinner size="sm" />
                          ) : (
                            <UserCheck className="w-4 h-4" />
                          )}
                        </button>
                        <button
                          onClick={() => handleDeactivateUser(user.id)}
                          disabled={actionLoading[`deactivate-${user.id}`]}
                          className="p-2 text-danger-600 hover:bg-danger-50 rounded-lg transition-colors disabled:opacity-50"
                          title="Refuser"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">Aucun compte en attente</p>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Actions rapides</h3>
            </div>
            <div className="p-6">
              <div className="space-y-3">
                <Link
                  to="/admin/products/new"
                  className="flex items-center p-3 bg-primary-50 text-primary-700 rounded-lg hover:bg-primary-100 transition-colors"
                >
                  <Plus className="w-5 h-5 mr-3" />
                  Ajouter un produit
                </Link>
                

                
                <Link
                  to="/admin/users"
                  className="flex items-center p-3 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                >
                  <Users className="w-5 h-5 mr-3" />
                  Gérer les utilisateurs
                </Link>
                
                <Link
                  to="/admin/transactions"
                  className="flex items-center p-3 bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <BarChart3 className="w-5 h-5 mr-3" />
                  Consulter les transactions
                </Link>
                
                <Link
                  to="/admin/users"
                  className="flex items-center p-3 bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 transition-colors"
                >
                  <DollarSign className="w-5 h-5 mr-3" />
                  Gérer les revendeurs
                </Link>
                

              </div>
            </div>
          </div>

          {/* Recharge Rapide */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center">
                <Wallet className="w-5 h-5 text-primary-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">Recharge rapide</h3>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {/* Recherche utilisateur */}
                <div className="relative">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rechercher un revendeur
                  </label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={quickRecharge.searchTerm}
                      onChange={(e) => handleSearchChange(e.target.value)}
                      placeholder="Nom, email ou entreprise..."
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                    {quickRecharge.isSearching && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        <LoadingSpinner size="sm" />
                      </div>
                    )}
                  </div>
                  
                  {/* Résultats de recherche */}
                  {quickRecharge.searchResults.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                      {quickRecharge.searchResults.map((user) => (
                        <button
                          key={user.id}
                          onClick={() => handleUserSelect(user)}
                          className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900">
                            {user.firstName} {user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {user.company} • {user.email}
                          </div>
                          <div className="text-xs text-gray-400">
                            Solde: {formatCurrency(user.balance || 0)}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Utilisateur sélectionné */}
                {quickRecharge.selectedUser && (
                  <div className="p-3 bg-primary-50 rounded-lg border border-primary-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-primary-900">
                          {quickRecharge.selectedUser.firstName} {quickRecharge.selectedUser.lastName}
                        </p>
                        <p className="text-sm text-primary-700">
                          {quickRecharge.selectedUser.company}
                        </p>
                        <p className="text-xs text-primary-600">
                          Solde actuel: {formatCurrency(quickRecharge.selectedUser.balance || 0)}
                        </p>
                      </div>
                      <button
                        onClick={() => setQuickRecharge(prev => ({ ...prev, selectedUser: null, searchTerm: '' }))}
                        className="text-primary-600 hover:text-primary-800"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                )}

                {/* Montant */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant à recharger (DA)
                  </label>
                  <input
                    type="number"
                    value={quickRecharge.amount}
                    onChange={(e) => setQuickRecharge(prev => ({ ...prev, amount: e.target.value }))}
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>

                {/* Note */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Note (optionnel)
                  </label>
                  <textarea
                    value={quickRecharge.note}
                    onChange={(e) => setQuickRecharge(prev => ({ ...prev, note: e.target.value }))}
                    placeholder="Raison de la recharge..."
                    rows={2}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none"
                  />
                </div>

                {/* Bouton de recharge */}
                <button
                  onClick={handleQuickRecharge}
                  disabled={!quickRecharge.selectedUser || !quickRecharge.amount || quickRecharge.isRecharging}
                  className="w-full flex items-center justify-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {quickRecharge.isRecharging ? (
                    <LoadingSpinner size="sm" className="mr-2" />
                  ) : (
                    <Wallet className="w-4 h-4 mr-2" />
                  )}
                  {quickRecharge.isRecharging ? 'Recharge en cours...' : 'Recharger le solde'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Activity className="h-5 w-5 mr-2 text-blue-600" />
              Activité récente
            </h3>
          </div>
          <div className="p-6">
            {stats.recentActivity.length > 0 ? (
              <div className="space-y-4">
                {stats.recentActivity.map((activity) => (
                  <div key={activity.id} className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg">
                    <div className="flex-shrink-0">
                      {activity.type === 'user_registration' ? (
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <Users className="h-4 w-4 text-blue-600" />
                        </div>
                      ) : (
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                          <Bell className="h-4 w-4 text-gray-600" />
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900">
                        {activity.title}
                      </p>
                      <p className="text-sm text-gray-500 truncate">
                        {activity.description}
                      </p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(activity.timestamp).toLocaleString('fr-FR')}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {activity.status === 'pending' && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <Clock className="h-3 w-3 mr-1" />
                          En attente
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500">Aucune activité récente</p>
                <p className="text-sm text-gray-400 mt-1">
                  L'historique des activités sera affiché ici
                </p>
              </div>
            )}
          </div>
        </div>




      </div>
    </div>
  );
};

export default AdminDashboard;
